import React, { createContext, useCallback, useEffect, useMemo, useState } from "react"
import { emitConsentTelemetry } from "./telemetry"
import { ConsentMode, resolveConsentMode } from "./geo"
import { isGpcEnabled } from "./gpc"

export const CONSENT_STORAGE_KEY = "activeloop_consent_v1"
export const CONSENT_POLICY_VERSION = 1

export type { ConsentMode } from "./geo"

export type ConsentCategories = {
  analytics: boolean
  marketing: boolean
}

export type ConsentRecord = ConsentCategories & {
  timestamp: number
  version: number
}

export type ConsentState =
  | { status: "unknown" }
  | { status: "undecided" }
  | { status: "decided"; record: ConsentRecord }

export type ConsentContextValue = {
  state: ConsentState
  /** Effective current grant, accounting for geo-scoped defaults. */
  granted: ConsentCategories
  consentMode: ConsentMode | null
  bannerOpen: boolean
  openBanner: () => void
  closeBanner: () => void
  acceptAll: () => void
  rejectAll: () => void
  saveCategories: (categories: ConsentCategories) => void
}

export const ConsentContext = createContext<ConsentContextValue | null>(null)

const readFromStorage = (): ConsentState => {
  if (typeof window === "undefined") return { status: "unknown" }
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY)
    if (!raw) return { status: "undecided" }
    const parsed = JSON.parse(raw) as Partial<ConsentRecord>
    if (
      !parsed ||
      parsed.version !== CONSENT_POLICY_VERSION ||
      typeof parsed.analytics !== "boolean" ||
      typeof parsed.marketing !== "boolean"
    ) {
      return { status: "undecided" }
    }
    return {
      status: "decided",
      record: {
        analytics: parsed.analytics,
        marketing: parsed.marketing,
        timestamp: typeof parsed.timestamp === "number" ? parsed.timestamp : Date.now(),
        version: CONSENT_POLICY_VERSION,
      },
    }
  } catch {
    return { status: "undecided" }
  }
}

const writeToStorage = (record: ConsentRecord) => {
  if (typeof window === "undefined") return
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record))
  } catch {
    // ignore quota / privacy-mode failures
  }
}

const KNOWN_TRACKING_COOKIES = [
  /^_ga/,
  /^_gid$/,
  /^_gcl/,
  /^_gat/,
  /^_fbp$/,
  /^ajs_/,
  /^__hstc$/,
  /^hubspotutk$/,
  /^__hssc$/,
  /^__hssrc$/,
  /^_hjSession/,
  /^_hjIncludedInSessionSample/,
  /^_hjFirstSeen/,
  /^_hjAbsoluteSessionInProgress/,
  /^_li_/,
  /^li_/,
  /^lidc$/,
  /^UserMatchHistory$/,
]

const KNOWN_TRACKING_LOCAL_STORAGE = [/^ajs_/, /^statsig_/, /^_ss_/, /^_li_/, /^mp_/]

const clearTrackingStorage = () => {
  if (typeof window === "undefined") return

  try {
    const cookies = document.cookie.split(";")
    for (const raw of cookies) {
      const name = raw.split("=")[0]?.trim()
      if (!name) continue
      if (!KNOWN_TRACKING_COOKIES.some((re) => re.test(name))) continue
      const host = window.location.hostname
      const rootDomain = host.replace(/^www\./, "")
      const expires = "Thu, 01 Jan 1970 00:00:00 GMT"
      document.cookie = `${name}=; expires=${expires}; path=/;`
      document.cookie = `${name}=; expires=${expires}; path=/; domain=${host};`
      document.cookie = `${name}=; expires=${expires}; path=/; domain=.${rootDomain};`
    }
  } catch {
    // ignore
  }

  try {
    const keys: string[] = []
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i)
      if (key && KNOWN_TRACKING_LOCAL_STORAGE.some((re) => re.test(key))) {
        keys.push(key)
      }
    }
    keys.forEach((k) => window.localStorage.removeItem(k))
  } catch {
    // ignore
  }
}

type ProviderProps = {
  children: React.ReactNode
  /**
   * Override the geo-based consent mode. When omitted, the provider does
   * its own client-side geo detection via Cloudflare's trace endpoint.
   * Useful for tests and SSR prerender.
   */
  consentMode?: ConsentMode
}

export const ConsentProvider: React.FC<ProviderProps> = ({ children, consentMode: explicitMode }) => {
  // Start as "unknown" so SSR and first client render agree (no banner flash).
  const [state, setState] = useState<ConsentState>({ status: "unknown" })
  const [bannerOpen, setBannerOpen] = useState(false)
  const [resolvedMode, setResolvedMode] = useState<ConsentMode | null>(explicitMode ?? null)

  // Resolve geo once per session unless an explicit mode was passed.
  // GPC overrides everything, a GPC-signaling browser is always strict.
  useEffect(() => {
    if (isGpcEnabled()) {
      setResolvedMode("strict")
      return
    }
    if (explicitMode) {
      setResolvedMode(explicitMode)
      return
    }
    let cancelled = false
    resolveConsentMode().then((m) => {
      if (!cancelled) setResolvedMode(m)
    })
    return () => {
      cancelled = true
    }
  }, [explicitMode])

  // On hydration, read storage and decide whether to open the banner.
  useEffect(() => {
    if (resolvedMode === null) return
    const next = readFromStorage()
    setState(next)
    if (next.status === "undecided" && resolvedMode === "strict") {
      setBannerOpen(true)
      emitConsentTelemetry("banner_shown", {
        geo: "strict",
        gpc: isGpcEnabled(),
      })
    }
  }, [resolvedMode])

  const persist = useCallback(
    (
      categories: ConsentCategories,
      {
        wasGranted,
        telemetryEvent,
      }: {
        wasGranted: ConsentCategories
        telemetryEvent: "banner_accepted" | "banner_rejected" | "banner_customized" | "banner_revoked"
      },
    ) => {
      const record: ConsentRecord = {
        ...categories,
        timestamp: Date.now(),
        version: CONSENT_POLICY_VERSION,
      }
      writeToStorage(record)
      setState({ status: "decided", record })
      setBannerOpen(false)

      const revokedAnalytics = wasGranted.analytics && !categories.analytics
      const revokedMarketing = wasGranted.marketing && !categories.marketing
      const isRevoking = revokedAnalytics || revokedMarketing

      emitConsentTelemetry(isRevoking ? "banner_revoked" : telemetryEvent, {
        analytics: categories.analytics,
        marketing: categories.marketing,
      })

      if (isRevoking) {
        clearTrackingStorage()
        // Force a reload so tracker SDKs loaded in memory are unloaded.
        if (typeof window !== "undefined") window.location.reload()
      }
    },
    [],
  )

  // Geo-scoped default: strict (CA / EU / UK / GPC) keeps trackers OFF
  // until explicit Accept (§ 638.51(b)(5) strict opt-in). Relaxed tracks
  // ON by default (CCPA-style opt-out via footer "Cookie Preferences").
  const currentGranted = useMemo<ConsentCategories>(
    () =>
      state.status === "decided"
        ? { analytics: state.record.analytics, marketing: state.record.marketing }
        : resolvedMode === "relaxed"
          ? { analytics: true, marketing: true }
          : { analytics: false, marketing: false },
    [state, resolvedMode],
  )

  const value = useMemo<ConsentContextValue>(
    () => ({
      state,
      granted: currentGranted,
      consentMode: resolvedMode,
      bannerOpen,
      openBanner: () => setBannerOpen(true),
      closeBanner: () => {
        setBannerOpen(false)
        emitConsentTelemetry("banner_dismissed")
      },
      acceptAll: () =>
        persist({ analytics: true, marketing: true }, { wasGranted: currentGranted, telemetryEvent: "banner_accepted" }),
      rejectAll: () =>
        persist({ analytics: false, marketing: false }, { wasGranted: currentGranted, telemetryEvent: "banner_rejected" }),
      saveCategories: (categories) =>
        persist(categories, { wasGranted: currentGranted, telemetryEvent: "banner_customized" }),
    }),
    [state, bannerOpen, persist, currentGranted, resolvedMode],
  )

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>
}
